import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useI18n } from "@project/lib/use-i18n";
import { PageShell } from "@project/user/components/page-shell";
import {
  ImageReveal,
  Reveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import {
  ShoppingBag,
  Star,
  Quote,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Heart,
  Plus,
  Leaf,
  Loader2,
  Phone,
  Gift,
  Store,
  Users,
  Home,
  Sprout,
  Minus,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  HeartHandshake,
} from "lucide-react";
import {
  GruhUdhyogOrderModal,
  type CartItem,
} from "@project/user/components/gruh-udhyog-order-modal";
import { GruhUdhyogFranchiseModal } from "@project/user/components/gruh-udhyog-franchise-modal";
import { GruhUdhyogEmploymentModal } from "@project/user/components/gruh-udhyog-employment-modal";
import { get } from "@project/services/shared/api-client";
import type { Product, ProductCategory, ProductVariant } from "@project/types/gruh-udhyog";
import { resolveMediaUrl } from "@project/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addToCart as reduxAddToCart,
  updateQuantity as reduxUpdateQuantity,
  removeItem as reduxRemoveItem,
  clearCart as reduxClearCart,
} from "@/store/slices/cartSlice";

import careImg from "@project/assets/home-care.webp";
import residentsImg from "@project/assets/home-residents.webp";
import gaushalaImg1 from "@project/assets/gruh-udhyog/GU-banner.png";
import gaushalaImg2 from "@project/assets/Gaushala/image (2).jfif";
import methiKhakhraImg from "@project/assets/gruh-udhyog/GU-main.png";
import franchiseCutoutImg from "@project/assets/gruh-udhyog/Gruh-udhyog-franchise.png";
import employmentArtisanImg from "@project/assets/gruh-udhyog/employment-artisan.png";

const artisans = [
  {
    nameKey: "programs.gruhUdhyog.artisans.a1Name",
    craftKey: "programs.gruhUdhyog.artisans.a1Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a1Years",
    image: careImg,
  },
  {
    nameKey: "programs.gruhUdhyog.artisans.a2Name",
    craftKey: "programs.gruhUdhyog.artisans.a2Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a2Years",
    image: residentsImg,
  },
  {
    nameKey: "programs.gruhUdhyog.artisans.a3Name",
    craftKey: "programs.gruhUdhyog.artisans.a3Craft",
    yearsKey: "programs.gruhUdhyog.artisans.a3Years",
    image: gaushalaImg2,
  },
];

function generatePaginationPages(
  currentPage: number,
  totalPages: number
): (number | "ellipsis")[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [];

  if (currentPage <= 3) {
    pages.push(1, 2, 3, 4, "ellipsis", totalPages);
  } else if (currentPage >= totalPages - 2) {
    pages.push(
      1,
      "ellipsis",
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages
    );
  } else {
    pages.push(
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages
    );
  }

  return pages;
}

/** Product card matching the exact site card shape with backend model mapping */
function ProductCard({
  product,
  cart,
  onAddToCart,
  onUpdateQuantity,
}: {
  product: Product;
  cart: CartItem[];
  onAddToCart: (product: Product, variant: ProductVariant) => void;
  onUpdateQuantity: (cartItemId: string, delta: number) => void;
}) {
  const activeVariants = useMemo(
    () => (product.variants ?? []).filter(v => v.is_active !== false),
    [product.variants]
  );
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [imgError, setImgError] = useState(false);

  const selectedVariant =
    activeVariants[
    Math.min(selectedVariantIdx, Math.max(activeVariants.length - 1, 0))
    ];

  const variantLabel = (v: ProductVariant) => v.value ?? v.weight ?? "";
  const variantStock = (v: ProductVariant) =>
    Number(v.available_stock ?? v.total_stock ?? v.stock ?? 0);
  const variantPrice = (v: ProductVariant) =>
    v.display_price ?? v.discounted_price ?? v.price;
  const hasDiscount = (v: ProductVariant) => {
    const discounted = Number(v.discounted_price);
    const base = Number(v.price);
    return (
      Number.isFinite(discounted) &&
      discounted > 0 &&
      Number.isFinite(base) &&
      discounted < base
    );
  };

  const cartItemId = selectedVariant
    ? `${product.id}-${variantLabel(selectedVariant).replace(/\s+/g, "")}`
    : `${product.id}-default`;
  const inCart = cart.find(item => item.id === cartItemId);

  if (!selectedVariant) {
    return null; // Don't render cards without variants
  }

  const stockLeft = variantStock(selectedVariant);
  const displayStockLeft = Math.max(0, stockLeft - (inCart?.quantity ?? 0));
  const isOutOfStock = stockLeft <= 0 || selectedVariant.in_stock === false;
  const isMaxStockReached = Boolean(inCart && inCart.quantity >= stockLeft);
  const imageSrc = resolveMediaUrl(product.image ?? product.image_url);
  const optionTitle = product.variant_master_details?.title?.trim() || "Weight";
  const displayPrice = variantPrice(selectedVariant);
  const showDiscount = hasDiscount(selectedVariant);

  return (
    <article className="border-gold/30 group flex h-full flex-col justify-between rounded-3xl border bg-card p-4 sm:p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-md">
      <div>
        {/* Medium-height product image */}
        <div className="bg-secondary/30 relative h-44 w-full overflow-hidden rounded-2xl sm:h-40">
          {imageSrc && !imgError ? (
            <img
              src={imageSrc}
              alt={product.product_name}
              onError={() => setImgError(true)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="bg-muted/40 flex h-full w-full items-center justify-center">
              <ShoppingBag className="text-muted-foreground/30 h-10 w-10" />
            </div>
          )}
          {product.is_handmade && (
            <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow-sm">
              Handmade
            </span>
          )}
          {product.is_signature && (
            <span className="bg-card/90 absolute right-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold text-amber-600 shadow-sm backdrop-blur-sm">
              <Star className="h-3.5 w-3.5 fill-current text-amber-300" />
              {/* Signature */}
            </span>
          )}
        </div>

        {/* Category line (Maker line removed) */}
        <div className="mt-2.5 flex items-center">
          <Badge
            variant="secondary"
            className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold"
          >
            {product.category_name ??
              product.category_details?.category_name ??
              "Product"}
          </Badge>
        </div>

        {/* Title & Details (Smaller, well-proportioned font) */}
        <h3 className="mt-2 font-serif !text-[20px] sm:!text-[25px] font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
          {product.product_name}
        </h3>
        <p className="mt-1 line-clamp-2 !text-xs leading-relaxed text-muted-foreground">
          {product.short_description ??
            product.description ??
            "No description provided."}
        </p>

        {/* Variant option pills (Compact without excessive spacing) */}
        <div className="mt-2.5 flex justify-start items-center gap-2 flex-wrap ">
          <span className=" block text-[10px] !font-medium uppercase tracking-wider text-gray-800">
            {optionTitle}:
          </span>
          <div className="flex flex-wrap gap-1.5">
            {activeVariants.map((v, idx) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setSelectedVariantIdx(idx)}
                className={`rounded-lg px-2.5 py-0.5 text-xs font-bold transition-all ${selectedVariantIdx === idx
                  ? "border border-sky-300/80 bg-sky-100 text-sky-900 shadow-xs dark:border-sky-700 dark:bg-sky-950/60 dark:text-sky-200"
                  : "border border-border/40 bg-secondary/70 text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
              >
                {variantLabel(v)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Price & Circular Add Action Footer */}
      <div className="border-border/60 mt-4 flex items-center justify-between border-t pt-3">
        <div>
          {/* <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Price
          </span> */}
          <div className="flex items-baseline gap-2">
            <span className="font-sans text-xl font-bold text-primary">
              ₹{Number(displayPrice).toFixed(2)}
            </span>
            {showDiscount && (
              <span className="text-xs text-muted-foreground line-through">
                ₹{Number(selectedVariant.price).toFixed(2)}
              </span>
            )}
          </div>
          {(isOutOfStock || displayStockLeft <= 10) && (
            <span className="mt-0.5 block text-[10px] font-semibold text-amber-600">
              {isOutOfStock || displayStockLeft <= 0
                ? "Out of Stock"
                : `Stock: ${displayStockLeft} left`}
            </span>
          )}
        </div>

        {isOutOfStock ? (
          <Button
            type="button"
            disabled
            className="rounded-full bg-muted px-3 text-xs font-semibold text-muted-foreground"
          >
            Sold Out
          </Button>
        ) : inCart ? (
          <div className="bg-secondary flex items-center rounded-full border border-primary p-0">
            <button
              type="button"
              onClick={() => onUpdateQuantity(cartItemId, -1)}
              className="hover:bg-primary/20 grid h-8 w-8 place-items-center rounded-full text-primary transition-colors"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-7 text-center text-xs font-bold text-primary">
              {inCart.quantity}
            </span>
            <button
              type="button"
              disabled={isMaxStockReached}
              onClick={() => onUpdateQuantity(cartItemId, 1)}
              className="hover:bg-primary/20 grid h-8 w-8 place-items-center rounded-full text-primary transition-colors disabled:opacity-30"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onAddToCart(product, selectedVariant)}
            title="Add to Cart"
            className="hover:bg-primary/90 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-all hover:scale-105"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </article>
  );
}

export default function GruhUdhyogShowcase() {
  const { t } = useI18n();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState<{ id: number | "All"; name: string }[]>([
    { id: "All", name: "All" },
  ]);
  const [selectedCatId, setSelectedCatId] = useState<number | "All">("All");
  const [sortBy, setSortBy] = useState<"popular" | "price-low" | "price-high">(
    "popular"
  );

  const dispatch = useAppDispatch();
  const currentUserKey = useAppSelector(state => state.cart.currentUserKey);
  const cart = useAppSelector(state => state.cart.carts[currentUserKey] || []);
  const [modalOpen, setModalOpen] = useState(false);
  const [franchiseModalOpen, setFranchiseModalOpen] = useState(false);
  const [employmentModalOpen, setEmploymentModalOpen] = useState(false);

  // Check search query parameter to automatically reopen shopping bag after login redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("bag") === "open" && cart.length > 0) {
      setModalOpen(true);
      // Clean up search query parameter from URL bar to keep it tidy
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [cart.length]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const catRes = await get<{
          success: boolean;
          data: ProductCategory[];
        }>("/jivandhara/product-categories/");

        const activeCategories = (catRes?.data ?? [])
          .filter(c => c.is_active !== false)
          .map(c => ({ id: c.id, name: c.category_name }));

        setCategories([{ id: "All", name: "All" }, ...activeCategories]);
      } catch (err) {
        console.error("Failed to load categories:", err);
      }
    };
    void fetchCategories();
  }, []);

  // Fetch products when selectedCategory changes
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url =
          selectedCatId === "All"
            ? "/jivandhara/products/"
            : `/jivandhara/products/?category=${selectedCatId}`;
        const res = await get<{
          success: boolean;
          data: Product[];
          pagination?: unknown;
        }>(url);
        const active = (res?.data ?? []).filter(p => p.is_active !== false);
        setProducts(active);
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        setLoading(false);
      }
    };
    void fetchProducts();
  }, [selectedCatId]);

  // Cart helper functions
  const addToCart = (product: Product, variant: ProductVariant) => {
    dispatch(reduxAddToCart({ product, variant }));
  };

  const updateQuantity = (cartItemId: string, delta: number) => {
    dispatch(reduxUpdateQuantity({ cartItemId, delta }));
  };

  const removeItem = (cartItemId: string) => {
    dispatch(reduxRemoveItem(cartItemId));
  };

  const clearCart = () => {
    dispatch(reduxClearCart());
  };

  const totalCartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const totalCartAmount = cart.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0
  );

  // Compute Signature Products (is_signature === true)
  const signatureProducts = useMemo(() => {
    return products.filter(p => p.is_signature === true);
  }, [products]);

  // Compute Catalog list (with filters and sorting)
  const catalogProducts = useMemo(() => {
    let result = products;

    if (sortBy === "price-low") {
      result = [...result].sort((a, b) => {
        const priceA = Number(
          a.variants?.[0]?.display_price ?? a.variants?.[0]?.price ?? 0
        );
        const priceB = Number(
          b.variants?.[0]?.display_price ?? b.variants?.[0]?.price ?? 0
        );
        return priceA - priceB;
      });
    } else if (sortBy === "price-high") {
      result = [...result].sort((a, b) => {
        const priceA = Number(
          a.variants?.[0]?.display_price ?? a.variants?.[0]?.price ?? 0
        );
        const priceB = Number(
          b.variants?.[0]?.display_price ?? b.variants?.[0]?.price ?? 0
        );
        return priceB - priceA;
      });
    }
    return result;
  }, [products, sortBy]);

  // Dynamic Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  // Reset pagination when category, sorting, or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCatId, sortBy, pageSize]);

  const totalPages = Math.max(1, Math.ceil(catalogProducts.length / pageSize));
  const paginatedCatalogProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return catalogProducts.slice(start, start + pageSize);
  }, [catalogProducts, currentPage, pageSize]);

  const scrollToCatalog = () => {
    document
      .getElementById("product-catalog")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      {/* 1. HERO SECTION */}
      <section className="bg-hero-gradient relative overflow-hidden text-primary-foreground shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]">
        <img
          src={gaushalaImg1}
          alt=""
          aria-hidden
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="from-primary/60 via-primary/45 to-gold/35 absolute inset-0 bg-gradient-to-br" />
        <div className="from-primary/50 to-primary/25 absolute inset-0 bg-gradient-to-t via-transparent" />

        <div className="relative mx-auto grid max-w-site items-center gap-10 px-4 py-12 sm:py-14 md:grid-cols-2 md:gap-12 md:py-16 lg:gap-16">
          <div className="text-left">
            <span
              className="bg-gold/25 border-gold/40 anim-fade-up inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-gold"
              style={{ animationDelay: "100ms" }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {t("programs.gruhUdhyog.hero.badge")}
            </span>

            <h1 className="mt-5 font-serif !text-4xl font-bold leading-tight drop-shadow sm:!text-5xl md:!text-[3.25rem]">
              <span className="anim-line" style={{ animationDelay: "220ms" }}>
                <span>{t("programs.gruhUdhyog.hero.brand")}</span>
              </span>
            </h1>

            <p
              className="anim-fade-up mt-4 text-base font-semibold leading-snug text-gold sm:text-lg"
              style={{ animationDelay: "380ms" }}
            >
              {t("programs.gruhUdhyog.hero.title")}
            </p>

            <p
              className="text-primary-foreground/95 anim-fade-up mt-4 max-w-xl text-sm leading-relaxed sm:text-base"
              style={{ animationDelay: "480ms" }}
            >
              {t("programs.gruhUdhyog.hero.subtitle")}
            </p>

            <div
              className="anim-fade-up mt-8 flex flex-wrap gap-3"
              style={{ animationDelay: "600ms" }}
            >
              <Button
                type="button"
                onClick={scrollToCatalog}
                className="hover:bg-gold/90 btn-lift h-11 gap-2 rounded-xl bg-gold px-6 text-xs font-bold uppercase tracking-wider text-gold-foreground shadow-lg"
              >
                <ShoppingBag className="h-4 w-4" />
                {t("programs.gruhUdhyog.hero.primaryCta")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  document
                    .getElementById("artisan-stories")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-primary-foreground/50 hover:bg-primary-foreground/10 btn-lift h-11 gap-2 rounded-xl bg-transparent px-5 font-semibold text-primary-foreground hover:text-primary-foreground"
              >
                <Heart className="h-4 w-4" />
                {t("programs.gruhUdhyog.hero.secondaryCta")}
              </Button>
            </div>
          </div>

          {/* Hero Banner Feature Image Card */}
          <div className="relative flex justify-end">
            <div
              className="bg-card/10 anim-zoom-in w-full max-w-lg overflow-hidden rounded-2xl border border-white/20 p-6 shadow-2xl ring-1 ring-white/20 backdrop-blur-md"
              style={{ animationDelay: "300ms" }}
            >
              <div
                className="anim-iris relative overflow-hidden rounded-xl"
                style={{ animationDelay: "480ms" }}
              >
                <img
                  src={methiKhakhraImg}
                  alt="Fresh Handmade Methi Khakhra"
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-foreground shadow-md">
                  Handcrafted with Love
                </span>
              </div>

              <div
                className="anim-fade-up mt-4 grid grid-cols-2 gap-3 text-xs text-white"
                style={{ animationDelay: "900ms" }}
              >
                <div className="flex items-center gap-2 rounded-lg bg-white/10 p-2.5 backdrop-blur-sm">
                  <Leaf className="h-4 w-4 shrink-0 text-gold" />
                  <span>100% Pure & Preservative Free</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-white/10 p-2.5 backdrop-blur-sm">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-gold" />
                  <span>100% Proceeds to Charity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT SECTION */}
      <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
        <Reveal variant="morph">
          <div className="grid overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-sm md:grid-cols-2">
            {/* Left Column: Eyebrow, Heading, Tagline & Feature Card */}
            <div className="flex flex-col justify-center gap-6 p-8 sm:p-10 lg:p-12">
              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  {t("programs.gruhUdhyog.about.eyebrow")}
                </p>
                <h2 className="font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  <RevealText delay={150}>
                    {t("programs.gruhUdhyog.about.heading")}
                  </RevealText>
                  <span className="mt-1 block font-sans text-lg font-medium text-muted-foreground">
                    {t("programs.gruhUdhyog.about.subheading")}
                  </span>
                </h2>
                <p className="text-base text-muted-foreground pt-1">
                  {t("programs.gruhUdhyog.about.tagline")}
                </p>
              </div>

              {/* Feature Card below title */}
              <div className="border-t border-border/70 pt-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {t("programs.gruhUdhyog.about.feature1Title")}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("programs.gruhUdhyog.about.feature1Desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Detailed Body Text */}
            <div className="flex flex-col justify-center border-t border-border/70 p-8 sm:p-10 md:border-l md:border-t-0 lg:p-12">
              <Reveal
                variant="right"
                delay={200}
                className="space-y-3.5 text-[0.95rem] leading-relaxed text-muted-foreground"
              >
                {t("programs.gruhUdhyog.about.body")
                  .split("\n\n")
                  .map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
              </Reveal>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Loading overlay for the whole store */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm font-medium text-muted-foreground">
              Loading fresh homemade snacks catalog…
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* 2. SIGNATURE PRODUCTS (is_signature === true) */}
          {signatureProducts.length > 0 && (
            <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
              <Reveal variant="morph" className="text-center">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                  Fresh Handmade Snacks
                </p>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  Our Signature Products
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Our absolute best sellers, handmade daily with premium local
                  ingredients.
                </p>
              </Reveal>

              <Stagger
                className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                delay={150}
              >
                {signatureProducts.map((p, i) => (
                  <StaggerItem key={p.id} index={i} from="zoom">
                    <ProductCard
                      product={p}
                      cart={cart}
                      onAddToCart={addToCart}
                      onUpdateQuantity={updateQuantity}
                    />
                  </StaggerItem>
                ))}
              </Stagger>
            </section>
          )}

          {/* 3. MAIN STORE CATALOG GRID */}
          <section
            id="product-catalog"
            className="border-border/40 bg-secondary/30 border-y py-14 sm:py-16"
          >
            <div className="mx-auto max-w-site px-4">
              <Reveal variant="left">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                    Store Catalog
                  </p>
                  <h2 className="mt-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                    Order Fresh Homemade Snacks
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Select your favorite snacks and place an online order
                    directly.
                  </p>
                </div>
              </Reveal>

              {/* Category Filters */}
              <Reveal
                variant="fade"
                delay={150}
                className="mt-8 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6"
              >
                <div className="flex flex-wrap gap-2">
                  {categories.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCatId(c.id)}
                      className={`rounded-full px-4 py-2 text-xs font-bold transition ${selectedCatId === c.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-foreground/70 hover:border-primary/40 border border-border bg-card hover:text-primary"
                        }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>

                {/* Sort dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    Sort by:
                  </span>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value as typeof sortBy)}
                    className="rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="popular">Popularity</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>
              </Reveal>

              {/* Products Catalog Grid */}
              <Stagger
                className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                step={70}
              >
                {paginatedCatalogProducts.map((p, i) => (
                  <StaggerItem key={p.id} index={i}>
                    {/* Products swapped in by a filter / page change fade up */}
                    <div
                      className="anim-fade-up h-full"
                      style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
                    >
                      <ProductCard
                        product={p}
                        cart={cart}
                        onAddToCart={addToCart}
                        onUpdateQuantity={updateQuantity}
                      />
                    </div>
                  </StaggerItem>
                ))}

                {catalogProducts.length === 0 && (
                  <div className="col-span-full py-16 text-center text-sm text-muted-foreground">
                    No products found in this category.
                  </div>
                )}
              </Stagger>

              {/* Dynamic Pagination Bar */}
              {catalogProducts.length > 0 && totalPages > 1 && (
                <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:flex-row">
                  {/* Results range info */}
                  <div className="text-xs text-muted-foreground">
                    Showing{" "}
                    <span className="font-semibold text-foreground">
                      {(currentPage - 1) * pageSize + 1}
                    </span>
                    –
                    <span className="font-semibold text-foreground">
                      {Math.min(currentPage * pageSize, catalogProducts.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-foreground">
                      {catalogProducts.length}
                    </span>{" "}
                    products
                  </div>

                  {/* Navigation Pages */}
                  <div className="flex items-center gap-1.5">
                    {/* Previous Button */}
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => {
                        setCurrentPage(p => Math.max(1, p - 1));
                        document
                          .getElementById("product-catalog")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="flex h-9 items-center gap-1 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground shadow-xs transition-all hover:bg-muted active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {generatePaginationPages(currentPage, totalPages).map(
                        (item, idx) =>
                          item === "ellipsis" ? (
                            <span
                              key={`ellipsis-${idx}`}
                              className="grid h-9 w-9 place-items-center text-xs font-bold text-muted-foreground"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={`page-${item}`}
                              type="button"
                              onClick={() => {
                                setCurrentPage(Number(item));
                                document
                                  .getElementById("product-catalog")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }}
                              className={`h-9 min-w-[36px] rounded-xl px-2 text-xs font-bold transition-all ${currentPage === item
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "border border-border bg-card text-foreground hover:border-primary/40 hover:text-primary"
                                }`}
                            >
                              {item}
                            </button>
                          )
                      )}
                    </div>

                    {/* Next Button */}
                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => {
                        setCurrentPage(p => Math.min(totalPages, p + 1));
                        document
                          .getElementById("product-catalog")
                          ?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="flex h-9 items-center gap-1 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground shadow-xs transition-all hover:bg-muted active:scale-95 disabled:pointer-events-none disabled:opacity-40"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Page Size Selector */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Show:</span>
                    <select
                      value={pageSize}
                      onChange={e => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="rounded-xl border border-border bg-card px-2.5 py-1 text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value={4}>4 per page</option>
                      <option value={8}>8 per page</option>
                      <option value={12}>12 per page</option>
                      <option value={16}>16 per page</option>
                      <option value={24}>24 per page</option>
                    </select>
                  </div>
                </div>
              )}

              <Reveal
                variant="wipe-right"
                className="mt-12 overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-card via-card to-amber-500/[0.06] p-6 shadow-sm sm:p-8"
              >
                <div className="flex flex-col items-center justify-between gap-6 text-center lg:flex-row lg:text-left">
                  {/* Left info & text */}
                  <div className="max-w-xl space-y-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1 text-xs font-bold text-primary">
                      <Gift className="h-3.5 w-3.5" />
                      <span>Bulk &amp; Festive Orders</span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-foreground sm:text-2xl">
                      Looking for custom bulk orders?
                    </h3>
                    <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      We prepare fresh, customized packages for weddings, religious occasions, and corporate gifting. Call our team directly or{" "}
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-1 font-bold text-primary underline underline-offset-4 transition-colors hover:text-primary/80"
                      >
                        contact our team <ArrowRight className="h-3 w-3" />
                      </Link>{" "}
                      for special bulk order pricing.
                    </p>
                  </div>

                  {/* Right: Phone number action cards */}
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <a
                      href="tel:+919662544565"
                      className="group flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3 text-xs font-bold text-foreground shadow-xs transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md"
                    >
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-white/20 group-hover:text-white">
                        <Phone className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <span className="block text-[10px] font-medium text-muted-foreground group-hover:text-white/80">Order Helpline 1</span>
                        <span className="text-sm font-extrabold tracking-wide">+91 96625 44565</span>
                      </div>
                    </a>

                    <a
                      href="tel:+919978930960"
                      className="group flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3 text-xs font-bold text-foreground shadow-xs transition-all hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md"
                    >
                      <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-white/20 group-hover:text-white">
                        <Phone className="h-4 w-4" />
                      </div>
                      <div className="text-left">
                        <span className="block text-[10px] font-medium text-muted-foreground group-hover:text-white/80">Order Helpline 2</span>
                        <span className="text-sm font-extrabold tracking-wide">+91 99789 30960</span>
                      </div>
                    </a>
                  </div>
                </div>
              </Reveal>


              {/* 6. FRANCHISE OPPORTUNITY SECTION */}
              <section
                id="franchise-opportunity"
                className="mx-auto max-w-site py-12 sm:py-16"
              >
                <Reveal variant="fade">
                  <div className="relative overflow-hidden rounded-[2.5rem] border border-[#2D5A3E]/15 bg-[#FAF7F2] p-6 shadow-sm sm:p-10 lg:py-8 lg:pt-2 lg:pl-12 lg:pr-0 lg:pb-0 dark:border-border dark:bg-card">
                    <div className="relative z-10 grid items-end gap-8 lg:grid-cols-12 lg:gap-8">
                      {/* Left Column: Text & Features & CTA */}
                      <Reveal
                        variant="left"
                        delay={100}
                        className="space-y-6 lg:col-span-7 lg:pb-8 lg:pt-4"
                      >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 rounded-full bg-[#E8F1EC] px-3.5 py-1.5 text-xs font-bold text-[#2C5E43] dark:bg-primary/10 dark:text-primary">
                          <Store className="h-4 w-4 text-[#2C5E43] dark:text-primary" />
                          <span>{t("programs.gruhUdhyog.franchise.badge")}</span>
                        </div>

                        {/* Heading */}
                        <h2 className="font-sans text-3xl font-extrabold tracking-tight text-[#1A2E22] sm:text-4xl lg:text-[40px] lg:leading-[1.15] dark:text-foreground">
                          {t("programs.gruhUdhyog.franchise.title")}{" "}
                          <span className="text-[#2D6A4F] dark:text-primary">
                            {t("programs.gruhUdhyog.franchise.titleHighlight")}
                          </span>
                        </h2>

                        {/* Description */}
                        <p className="max-w-xl text-base leading-relaxed text-[#4A5D50] sm:text-[16px] dark:text-muted-foreground">
                          {t("programs.gruhUdhyog.franchise.description")}
                        </p>

                        {/* 3 Value Pillars */}
                        <div className="grid grid-cols-1 gap-3.5 pt-1 sm:grid-cols-3 sm:gap-3">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E4EFE8] text-[#2C5E43] shadow-xs dark:bg-primary/15 dark:text-primary">
                              <Home className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.franchise.feature1")}
                            </span>
                          </div>

                          <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E4EFE8] text-[#2C5E43] shadow-xs dark:bg-primary/15 dark:text-primary">
                              <Users className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.franchise.feature2")}
                            </span>
                          </div>

                          {/* <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E4EFE8] text-[#2C5E43] shadow-xs dark:bg-primary/15 dark:text-primary">
                              <Sprout className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.franchise.feature3")}
                            </span>
                          </div> */}
                        </div>

                        {/* CTA Button */}
                        <div className="pt-2">
                          <Button
                            type="button"
                            onClick={() => setFranchiseModalOpen(true)}
                            className="btn-lift group h-12 rounded-full bg-[#1C5135] px-8 text-sm font-bold text-white shadow-lg shadow-[#1C5135]/20 transition-all hover:bg-[#153f29] hover:shadow-xl dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
                          >
                            <span>{t("programs.gruhUdhyog.franchise.cta")}</span>
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </Button>
                        </div>
                      </Reveal>

                      {/* Right Column: Visual Artwork positioned cleanly on the right */}
                      <Reveal
                        variant="right"
                        delay={250}
                        className="relative flex items-end justify-center self-end pt-4 sm:pt-6 lg:col-span-5 lg:justify-end lg:pt-0"
                      >
                        <div className="relative flex w-full max-w-[360px] sm:max-w-[390px] lg:max-w-[410px] items-end justify-end">
                          {/* Organic Peak / Hill-shaped background SVG */}
                          <svg
                            className="pointer-events-none absolute -left-10 -top-8 h-[125%] w-[125%] select-none sm:left-12 sm:-top-[3.6rem]"
                            viewBox="0 0 520 500"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {/* Main contoured hill blob */}
                            <path
                              d="M 60 410 
                         C 10 320, 15 220, 65 160 
                         C 105 105, 175 65, 235 30 
                         C 265 12, 290 28, 315 65 
                         C 345 110, 385 170, 395 240 
                         C 410 320, 370 410, 270 445 
                         C 180 465, 110 455, 60 410 Z"
                              fill="#E2EDE5"
                              className="dark:fill-emerald-950/40"
                            />
                            {/* Secondary soft base shadow on lower left */}
                            <ellipse
                              cx="130"
                              cy="435"
                              rx="110"
                              ry="32"
                              fill="#E2EDE5"
                              opacity="0.8"
                              className="dark:fill-emerald-950/30"
                            />
                          </svg>

                          {/* House with leaves outline icon (Top Left inside the green hill) */}
                          <div className="pointer-events-none absolute left-0 top-2 z-0 select-none opacity-50 sm:left-[5.25rem] sm:top-12">
                            <svg
                              className="h-20 w-20 text-[#6F987C] opacity-85 sm:h-24 sm:w-24 lg:h-28 lg:w-28"
                              viewBox="0 0 140 140"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              {/* Chimney */}
                              <path
                                d="M96 44V24H110V54"
                                stroke="currentColor"
                                strokeWidth="4.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* Roof apex with overhangs */}
                              <path
                                d="M70 16L18 56V57H122V56L70 16Z"
                                stroke="currentColor"
                                strokeWidth="4.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* House body */}
                              <path
                                d="M28 57V118H112V57"
                                stroke="currentColor"
                                strokeWidth="4.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {/* Ground line */}
                              <path
                                d="M10 118H130"
                                stroke="currentColor"
                                strokeWidth="4.5"
                                strokeLinecap="round"
                              />
                              {/* Plant Stem */}
                              <path
                                d="M70 114V86"
                                stroke="currentColor"
                                strokeWidth="3.5"
                                strokeLinecap="round"
                              />
                              {/* Left Leaf */}
                              <path
                                d="M69 98C69 82 52 72 44 78C36 84 46 102 69 98Z"
                                fill="currentColor"
                              />
                              {/* Right Leaf */}
                              <path
                                d="M70 90C70 70 90 60 98 68C106 76 90 100 70 90Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>

                          {/* Handwritten script slogan (Top Right) */}
                          <div className="pointer-events-none absolute right-1 top-0.5 z-10 select-none text-right sm:right-2 sm:-top-[3.75rem]">
                            <p
                              className="font-serif text-sm font-bold italic tracking-wide text-[#1C5135] sm:text-base lg:text-lg dark:text-emerald-400"
                              style={{ transform: "rotate(-3deg)" }}
                            >
                              Start • Grow •
                            </p>
                            <p
                              className="font-serif text-sm font-bold italic tracking-wide text-[#1C5135] sm:text-base lg:text-lg dark:text-emerald-400"
                              style={{ transform: "rotate(-3deg)" }}
                            >
                              Make an Impact
                            </p>
                            <svg
                              className="ml-auto mt-0.5 h-2 w-20 text-[#1c5135da] sm:w-24 lg:w-28 dark:text-emerald-400"
                              viewBox="0 0 120 10"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                d="M2 6 Q 60 1, 118 7"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>

                          {/* Cutout Image of Artisan packaging - normalized size 300px on lg */}
                          <div className="relative z-10 flex items-end justify-end">
                            <img
                              src={franchiseCutoutImg}
                              alt="Gruh Udhyog Franchise Partner"
                              className="max-h-[250px] sm:max-h-[280px] lg:max-h-[300px] w-auto select-none object-contain drop-shadow-md"
                            />
                          </div>
                        </div>
                      </Reveal>
                    </div>
                  </div>
                </Reveal>
              </section>

              {/* 7. EMPLOYMENT OPPORTUNITIES SECTION */}
              <section
                id="employment-opportunities"
                className="mx-auto max-w-site pt-2 pb-12 sm:pb-16"
              >
                <Reveal variant="fade">
                  <div className="relative overflow-hidden rounded-[2.5rem] border border-[#2D5A3E]/15 bg-[#FAF7F2] p-6 shadow-sm sm:p-10 lg:py-8 lg:pt-2 lg:pl-0 lg:pr-8 lg:pb-0 dark:border-border dark:bg-card">
                    <div className="relative z-10 grid items-end gap-8 lg:grid-cols-12 lg:gap-8">
                      {/* Left Column: Visual Artwork positioned cleanly on the LEFT */}
                      <Reveal
                        variant="left"
                        delay={250}
                        className="order-2 relative flex items-end justify-center self-end pt-4 sm:pt-6 lg:order-1 lg:col-span-5 lg:justify-start lg:pt-0"
                      >
                        <div className="relative flex w-full max-w-[360px] sm:max-w-[390px] lg:max-w-[410px] items-end justify-start">
                          {/* Organic Peak / Hill-shaped background SVG */}
                          <svg
                            className="pointer-events-none absolute -left-8 -top-8 h-[125%] w-[125%] select-none sm:-left-6 sm:-top-[3.6rem]"
                            viewBox="0 0 520 500"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M 60 410 
                         C 10 320, 15 220, 65 160 
                         C 105 105, 175 65, 235 30 
                         C 265 12, 290 28, 315 65 
                         C 345 110, 385 170, 395 240 
                         C 410 320, 370 410, 270 445 
                         C 180 465, 110 455, 60 410 Z"
                              fill="#FEF3C7"
                              className="dark:fill-amber-950/40"
                            />
                            <ellipse
                              cx="240"
                              cy="435"
                              rx="130"
                              ry="32"
                              fill="#FEF3C7"
                              opacity="0.8"
                              className="dark:fill-amber-950/30"
                            />
                          </svg>

                          {/* Handwritten script slogan */}
                          <div className="pointer-events-none absolute right-1 top-0.5 z-10 select-none text-right sm:right-3 sm:-top-[3.75rem]">
                            <p
                              className="font-serif text-sm font-bold italic tracking-wide text-amber-800 sm:text-base lg:text-lg dark:text-amber-300"
                              style={{ transform: "rotate(-3deg)" }}
                            >
                              Earn • Uplift •
                            </p>
                            <p
                              className="font-serif text-sm font-bold italic tracking-wide text-amber-800 sm:text-base lg:text-lg dark:text-amber-300"
                              style={{ transform: "rotate(-3deg)" }}
                            >
                              Live with Dignity
                            </p>
                            <svg
                              className="ml-auto mt-0.5 h-2 w-20 text-amber-700/60 sm:w-24 lg:w-28 dark:text-amber-400"
                              viewBox="0 0 120 10"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                d="M2 6 Q 60 1, 118 7"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>

                          {/* Cutout Image of Artisan - flush on bottom-left edge */}
                          <div className="relative z-10 flex items-end justify-start">
                            <img
                              src={employmentArtisanImg}
                              alt="Gruh Udhyog Artisan Employment"
                              className="max-h-[250px] sm:max-h-[280px] lg:max-h-[300px] w-auto select-none object-contain drop-shadow-md"
                            />
                          </div>
                        </div>
                      </Reveal>

                      {/* Right Column: Text & Features & CTA */}
                      <Reveal
                        variant="right"
                        delay={100}
                        className="order-1 space-y-6 lg:order-2 lg:col-span-7 lg:pb-8 lg:pt-4"
                      >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-3.5 py-1.5 text-xs font-bold text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                          <Briefcase className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                          <span>{t("programs.gruhUdhyog.employment.badge")}</span>
                        </div>

                        {/* Heading */}
                        <h2 className="font-sans text-3xl font-extrabold tracking-tight text-[#1A2E22] sm:text-4xl lg:text-[40px] lg:leading-[1.15] dark:text-foreground">
                          {t("programs.gruhUdhyog.employment.title")}{" "}
                          <span className="text-amber-700 dark:text-amber-400">
                            {t("programs.gruhUdhyog.employment.titleHighlight")}
                          </span>
                        </h2>

                        {/* Description */}
                        <p className="max-w-xl text-base leading-relaxed text-[#4A5D50] sm:text-[16px] dark:text-muted-foreground">
                          {t("programs.gruhUdhyog.employment.description")}
                        </p>

                        {/* 3 Value Pillars */}
                        <div className="grid grid-cols-1 gap-3.5 pt-1 sm:grid-cols-3 sm:gap-3">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 shadow-xs dark:bg-amber-500/20 dark:text-amber-300">
                              <HeartHandshake className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.employment.feature1")}
                            </span>
                          </div>

                          <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 shadow-xs dark:bg-amber-500/20 dark:text-amber-300">
                              <Sparkles className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.employment.feature2")}
                            </span>
                          </div>

                          {/* <div className="flex items-center gap-2.5">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 shadow-xs dark:bg-amber-500/20 dark:text-amber-300">
                              <ShieldCheck className="h-4.5 w-4.5" />
                            </div>
                            <span className="text-xs font-bold text-[#1E3A2B] dark:text-foreground/90">
                              {t("programs.gruhUdhyog.employment.feature3")}
                            </span>
                          </div> */}
                        </div>

                        {/* CTA Button */}
                        <div className="pt-2">
                          <Button
                            type="button"
                            onClick={() => setEmploymentModalOpen(true)}
                            className="btn-lift group h-12 rounded-full bg-amber-700 px-8 text-sm font-bold text-white shadow-lg shadow-amber-700/20 transition-all hover:bg-amber-800 hover:shadow-xl dark:bg-amber-600 dark:hover:bg-amber-700"
                          >
                            <span>{t("programs.gruhUdhyog.employment.cta")}</span>
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </Button>
                        </div>
                      </Reveal>
                    </div>
                  </div>
                </Reveal>
              </section>
            </div>
          </section>
        </>
      )}

      {/* 5. ARTISAN STORIES */}
      <section
        id="artisan-stories"
        className="mx-auto max-w-site px-4 py-14 sm:py-16"
      >
        <Reveal variant="fade" className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("programs.gruhUdhyog.artisans.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            <RevealText delay={120}>
              {t("programs.gruhUdhyog.artisans.heading")}
            </RevealText>
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("programs.gruhUdhyog.artisans.subtitle")}
          </p>
        </Reveal>

        <Stagger className="mt-10 grid gap-6 md:grid-cols-3" step={140}>
          {artisans.map((a, i) => (
            <StaggerItem key={a.nameKey} index={i}>
              <figure className="group flex h-full flex-col justify-between rounded-3xl border border-border bg-card p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-md">
                <div>
                  <div className="bg-secondary/40 relative aspect-[4/3] overflow-hidden rounded-2xl">
                    <ImageReveal
                      direction={i % 2 === 0 ? "left" : "right"}
                      delay={200 + i * 140}
                      className="h-full w-full"
                    >
                      <img
                        src={a.image}
                        alt={t(a.nameKey)}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </ImageReveal>
                    <span className="absolute bottom-3 left-3 rounded-full bg-black/60 px-3 py-1 text-[10px] font-bold text-white backdrop-blur-md">
                      {t(a.yearsKey)}
                    </span>
                  </div>

                  <Quote className="mt-5 h-6 w-6 text-gold" />
                  <blockquote className="mt-2 font-serif text-base italic leading-snug text-foreground">
                    {t("programs.gruhUdhyog.artisans.quote")}
                  </blockquote>
                </div>

                <figcaption className="mt-6 border-t border-border pt-4">
                  <div className="text-sm font-bold text-foreground">
                    {t(a.nameKey)}
                  </div>
                  <div className="text-xs font-medium text-primary">
                    {t(a.craftKey)}
                  </div>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </section>


      {/* STICKY FLOATING CART BADGE */}
      {totalCartCount > 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-4 fixed bottom-6 right-6 z-40 duration-300">
          <Button
            type="button"
            onClick={() => setModalOpen(true)}
            className="hover:bg-primary/90 h-14 gap-3 rounded-full border-2 border-white/20 bg-primary px-6 text-primary-foreground shadow-2xl"
          >
            <div className="relative">
              <ShoppingBag className="h-6 w-6 text-gold" />
              <span className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-gold text-[10px] font-bold text-gold-foreground">
                {totalCartCount}
              </span>
            </div>
            <div className="text-left leading-tight">
              <div className="text-[10px] font-bold uppercase text-gold">
                View Bag
              </div>
              <div className="text-sm font-bold">₹{totalCartAmount}</div>
            </div>
          </Button>
        </div>
      )}

      {/* CART & ORDER MODAL */}
      <GruhUdhyogOrderModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        cart={cart}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeItem}
        onClearCart={clearCart}
      />

      {/* FRANCHISE ENQUIRY MODAL */}
      <GruhUdhyogFranchiseModal
        open={franchiseModalOpen}
        onOpenChange={setFranchiseModalOpen}
      />

      {/* EMPLOYMENT OPPORTUNITY MODAL */}
      <GruhUdhyogEmploymentModal
        open={employmentModalOpen}
        onOpenChange={setEmploymentModalOpen}
      />
    </PageShell>
  );
}
